<template>
    <card>
        <div class="d-flex justify-content-between align-items-start">
            <div>
                <h5 class="form-title">{{ title }}</h5>
                <p class="form-subtitle">{{ subtitle }}</p>
            </div>
            <span v-if="statusBadge" :class="statusBadgeClass">{{ statusBadge }}</span>
        </div>

        <div class="form-group">
            <label class="field-label">Channel</label>
            <input type="text" class="form-control readonly-field" :value="formData.channel" readonly />
        </div>

        <div class="row">
            <div class="col-md-6">
                <div class="form-group">
                    <label class="field-label">Marketplace</label>
                    <select
                        class="form-control"
                        :class="{ 'readonly-field': hasItems }"
                        v-model="formData.channelType"
                        :disabled="loadingBtnGetData || disabled || hasItems"
                    >
                        <option value="" disabled>Pilih Marketplace</option>
                        <option
                            v-for="opt in channelTypeOptions"
                            :key="opt.type"
                            :value="opt.type"
                        >{{ opt.typeName }}</option>
                    </select>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label class="field-label">Tanggal</label>
                    <input
                        type="date"
                        class="form-control"
                        :class="{ 'readonly-field': hasItems && !canEditDate }"
                        v-model="formData.disposalDate"
                        :disabled="loadingBtnGetData || disabled || (hasItems && !canEditDate)"
                        @change="$emit('change-date', formData.disposalDate)"
                    />
                </div>
            </div>
        </div>

        <div class="form-group">
            <label class="field-label">Order ID Marketplace</label>
            <div class="d-flex">
                <input
                    type="text"
                    class="form-control"
                    placeholder="Masukan Order ID Marketplace"
                    v-model="formData.trxId"
                    :disabled="loadingBtnGetData || disabled || formData.statusDisposal"
                />
                <button
                    v-if="!formData.statusDisposal || formData.items.length == 0"
                    class="btn btn-primary ml-2 ambil-data-btn"
                    :disabled="!isFormComplete || loadingBtnGetData"
                    @click="$emit('ambil-data')"
                >
                    {{ loadingBtnGetData ? 'Memuat data...' : 'Ambil Data' }}
                </button>
            </div>
        </div>

        <hr v-if="formData.salesInvoiceId || validationStatus" class="section-divider" />

        <validation-progress-box
            v-if="validationProgressData && showValidationProgress"
            :total="validationProgressData.totalData"
            :validated="validationProgressData.totalComplete"
            :is-valid="validationStatus.isValid"
            @refresh="$emit('get-validaition-progress')"
        />

        <validation-failed-box
            v-if="!validationStatus.isValid && validationStatus.failedItems && validationStatus.failedItems.length"
            :failed-items="validationStatus.failedItems"
            :loading="loadingRevalidate"
            :show-action="validationShowAction"
            @validasi-ulang="$emit('validasi-ulang')"
            @aksi-item="(item, idx) => $emit('aksi-item', item, idx)"
        />

        <div class="form-group" v-if="formData.salesInvoiceId">
            <label class="field-label">Sales Invoice ID</label>
            <input type="text" class="form-control readonly-field" :value="formData.salesInvoiceId" readonly />
        </div>

        <div class="form-group" v-if="hasItems">
            <label class="field-label">Item ({{ formData.items.length }})</label>
            <div
                v-for="(item, itemIndex) in formData.items"
                :key="`item-${itemIndex}`"
                class="item-block"
            >
                <div class="item-block-header d-flex justify-content-between align-items-start">
                    <div>
                        <p class="item-name-label mb-1">Nama Item</p>
                        <p class="item-name mb-0">{{ item.name }}</p>
                    </div>
                    <span v-if="itemProblemCount(item) > 0" class="item-problem-badge">
                        {{ item.rows.length }} Unit ({{ itemProblemCount(item) }} Bermasalah)
                    </span>
                    <span v-else class="item-unit-badge">{{ item.rows.length }} Unit</span>
                </div>
                <div class="item-source-block mb-4">
                    <p class="item-name-label mb-2">Nama Item pada Tiap Sumber</p>
                    <div class="item-source-list">
                        <div class="item-source-row d-flex align-items-center" :class="{ 'item-source-row-error': !item.erpName }">
                            <span class="item-source-badge">ERP</span>
                            <span v-if="item.erpName" class="item-source-name">{{ item.erpName }}</span>
                            <span v-else class="item-source-name-error">Item tidak ditemukan.</span>
                        </div>
                        <div class="item-source-row d-flex align-items-center" :class="{ 'item-source-row-error': !item.jurnalName }">
                            <span class="item-source-badge">JURNAL</span>
                            <span v-if="item.jurnalName" class="item-source-name">{{ item.jurnalName }}</span>
                            <span v-else class="item-source-name-error">Item tidak ditemukan.</span>
                        </div>
                    </div>
                </div>
                <p class="item-name-label mb-2">Detail</p>
                <div class="item-table-wrapper">
                    <table class="table table-sm item-table mb-0">
                        <thead>
                            <tr>
                                <th>SERIAL NUMBER</th>
                                <th>ASSET ID</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(row, rowIndex) in item.rows"
                                :key="`item-${itemIndex}-row-${rowIndex}`"
                                :class="{ 'row-failed': isSerialNumberFailed(row) }"
                            >
                                <td>{{ row.serialNumber }}</td>
                                <td>{{ row.assetId }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="form-group" v-if="hasAccessories">
            <label class="field-label">Aksesoris ({{ formData.accessories.length }})</label>
            <div class="accessory-block">
                <div class="accessory-row accessory-row-label d-flex align-items-center">
                    <span class="accessory-label accessory-col-code">Nomor Item</span>
                    <span class="accessory-label accessory-col-name">Nama Aksesoris</span>
                    <span class="accessory-label accessory-col-qty">Qty</span>
                </div>
                <div
                    v-for="(accessory, accIndex) in formData.accessories"
                    :key="`accessory-${accIndex}`"
                    class="accessory-row d-flex align-items-center"
                    :class="{ 'row-failed': isAccessoryFailed(accessory.itemCode) }"
                >
                    <span class="accessory-code accessory-col-code">{{ accessory.itemCode || '-' }}</span>
                    <span class="accessory-name accessory-col-name">{{ accessory.name }}</span>
                    <span class="accessory-col-qty">
                        <input
                            type="text"
                            class="form-control form-control-sm qty-input readonly-field"
                            :value="accessory.qty"
                            readonly
                            disabled
                        />
                    </span>
                </div>
            </div>
        </div>
    </card>
</template>

<script>
import { Card } from '../../Layout';
import ValidationProgressBox from './ValidationProgressBox.vue';
import ValidationFailedBox from './ValidationFailedBox.vue';

export default {
    name: 'DisposalForm',
    components: {
        Card,
        ValidationProgressBox,
        ValidationFailedBox,
    },
    props: {
        formData: {
            type: Object,
            required: true,
        },
        channelTypeOptions: {
            type: Array,
            default: () => [],
        },
        loadingBtnGetData: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: 'Detail Disposal',
        },
        subtitle: {
            type: String,
            default: 'Informasi detail mengenai disposal barang.',
        },
        statusBadge: {
            type: String,
            default: '',
        },
        validationStatus: {
            type: Object,
            default: null,
        },
        validationProgressData: {
            type: Object,
            default: null
        },
        loadingRevalidate: {
            type: Boolean,
            default: false,
        },
        showValidationProgress: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        hasItems() {
            return !!(this.formData.items && this.formData.items.length);
        },
        hasAccessories() {
            return !!(this.formData.accessories && this.formData.accessories.length);
        },
        canEditDate() {
            return ['draft', 'pending'].includes((this.formData.statusDisposal || '').toLowerCase());
        },
        isFormComplete() {
            return (
                (this.formData.channel || '').trim() !== '' &&
                (this.formData.channelType || '').trim() !== '' &&
                (this.formData.disposalDate || '').trim() !== '' &&
                (this.formData.trxId || '').trim() !== ''
            );
        },
        statusBadgeClass() {
            const map = {
                pending: 'status-badge status-badge-pending',
                partial: 'status-badge status-badge-partial',
                done: 'status-badge status-badge-done',
                failed: 'status-badge status-badge-failed',
            };
            return map[(this.statusBadge || '').toLowerCase()] || 'status-badge status-badge-pending';
        },
        failedItems() {
            return (this.validationStatus && this.validationStatus.failedItems) || [];
        },
        failedSerialNumbers() {
            return this.failedItems
                .filter((item) => (item.category || '').toLowerCase() !== 'aksesoris')
                .map((item) => item.reference);
        },
        failedAccessoryCodes() {
            return this.failedItems
                .filter((item) => (item.category || '').toLowerCase() === 'aksesoris')
                .map((item) => item.reference);
        },
    },
    methods: {
        isSerialNumberFailed(row) {
            return this.failedSerialNumbers.includes(row.serialNumber) || row.statusJurnal == 'failed';
        },
        isAccessoryFailed(itemCode) {
            return this.failedAccessoryCodes.includes(itemCode);
        },
        itemProblemCount(item) {
            return (item.rows || []).filter((row) => this.isSerialNumberFailed(row.serialNumber)).length;
        },
    },
};
</script>

<style scoped>
.form-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 4px;
    color: #212529;
}

.form-subtitle {
    font-size: 13px;
    color: #6c757d;
    margin-bottom: 20px;
}

.field-label {
    font-size: 13px;
    font-weight: 500;
    color: #343a40;
    margin-bottom: 4px;
}

.readonly-field {
    background-color: #f8f9fa;
    color: #6c757d;
}

.ambil-data-btn {
    white-space: nowrap;
    min-width: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.section-divider {
    border: none;
    border-top: 1px solid #e9ecef;
    margin: 20px 0;
}

.item-block {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    background: #fff;
}

.item-block-header {
    padding-bottom: 12px;
    border-bottom: 1px solid #e9ecef;
}

.item-name-label {
    font-size: 12px;
    color: #868e96;
    margin-bottom: 2px;
}

.item-name {
    font-size: 13px;
    font-weight: 600;
    color: #212529;
}

.item-problem-badge {
    display: inline-block;
    padding: 3px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    color: #dc3545;
    background: #fdecea;
    border: 1px solid #f6c6c8;
    white-space: nowrap;
}

.item-unit-badge {
    display: inline-block;
    padding: 3px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    color: #495057;
    background: #eef1f4;
    border: 1px solid #dfe4e8;
    white-space: nowrap;
}

.item-source-block {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
}

.item-source-list {
    border: 1px solid #e9ecef;
    border-left: 4px solid #94A3B8;
    border-radius: 0 7px 7px 0;
    overflow: hidden;
    background: #F4F6F8;
}

.item-source-row {
    padding: 10px 16px;
    border-bottom: 1px solid #e9ecef;
}

.item-source-row:last-child {
    border-bottom: none;
}

.item-source-badge {
    display: inline-block;
    width: 68px;
    text-align: center;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    color: #495057;
    background: #fff;
    border: 1px solid #dfe4e8;
    margin-right: 12px;
    flex-shrink: 0;
}

.item-source-name {
    font-size: 13px;
    color: #212529;
}

.item-source-row-error {
    background-color: #FFF4F4;
}

.item-source-name-error {
    font-size: 13px;
    font-style: italic;
    color: #8A3138;
}

.row-failed {
    background-color: #fdecea;
}

.item-table-wrapper {
    margin-top: 16px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
}

.item-table {
    border: none;
    border-radius: 0;
    margin: 0;
    table-layout: fixed;
}

.item-table th,
.item-table td {
    width: 50%;
}

.item-table thead th {
    background-color: #f8f9fa;
    font-size: 11px;
    font-weight: 700;
    color: #868e96;
    text-transform: uppercase;
    text-align: center;
    border-top: none;
    border-bottom: 1px solid #e9ecef;
    border-right: 1px solid #e9ecef;
    padding: 10px 16px;
}

.item-table tbody td {
    font-size: 13px;
    color: #343a40;
    vertical-align: middle;
    border-top: none;
    border-bottom: 1px solid #f1f3f5;
    border-right: 1px solid #f1f3f5;
    padding: 12px 16px;
}

.item-table thead th:last-child,
.item-table tbody td:last-child {
    border-right: none;
}

.item-table tbody tr:last-child td {
    border-bottom: none;
}

.accessory-block {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 16px;
    background: #fff;
}

.accessory-row {
    padding: 12px 0;
    border-bottom: 1px solid #f1f3f5;
}

.accessory-row:first-child {
    padding-top: 0;
}

.accessory-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.accessory-row-label {
    border-bottom: none;
    padding-bottom: 8px;
}

.accessory-label {
    font-size: 12px;
    color: #868e96;
}

.accessory-code {
    font-size: 13px;
    color: #495057;
}

.accessory-name {
    font-size: 13px;
    font-weight: 600;
    color: #212529;
}

.accessory-col-code {
    width: 90px;
    flex-shrink: 0;
}

.accessory-col-name {
    flex: 1;
    padding-right: 12px;
}

.accessory-col-qty {
    width: 70px;
    flex-shrink: 0;
    text-align: right;
}

.qty-input {
    width: 60px;
    text-align: center;
}

.status-badge {
    display: inline-block;
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid transparent;
    white-space: nowrap;
}

.status-badge-pending {
    color: #6c757d;
    background: #eef1f4;
    border-color: #dfe4e8;
}

.status-badge-partial {
    color: #c98a06;
    background: #fff6e0;
    border-color: #f5e2ad;
}

.status-badge-done {
    color: #28a745;
    background: #e8f8ee;
    border-color: #c3ecd2;
}

.status-badge-failed {
    color: #dc3545;
    background: #fdecea;
    border-color: #f6c6c8;
}
</style>



// WEBPACK FOOTER //
// src/components/Disposal/CustomComponent/DisposalForm.vue